import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Link as RouterLink } from 'react-router-dom';
import { PreviewEntry, Run, approvedPrompt, buildRuns, previewConfigKey, runKey } from './previewRuns';

import StaticAdsService from 'services/StaticAdsService';

import { bumpStaticActivity } from './activityBus';
import ProductAssetPicker from './ProductAssetPicker';
import { GenerationSimilarity, ProductAsset, ProductSummary } from './types';

// Kick off image generation: this ad's structure, that product's references.
//
// The product choice is the whole decision. Its reference photos are attached
// to the generation and declared ground truth for the product, and its brief
// becomes the remake direction (adapt mode). No product means clone mode — the
// same ad rebuilt as-is, competitor marks swapped out.
//
// Single-ad generations can PREVIEW the exact prompt first (runs stage 2 once,
// ~30-60s) and hand-edit it; the approved prompt rides along on the queue call
// so the worker sends it verbatim without re-running or re-billing stage 2.

// The five variation axes — how far each run departs from the source ad. One
// generation is queued per (ad × axis). faithful and copy_swap attach the
// source ad image as a reference; the others deliberately withhold it.
// Hard cap per submit: ads × variations. Keeps a casual batch from quietly
// spending a stack of Higgsfield credits in one click.
const MAX_RUNS_PER_SUBMIT = 5;


const VARIATION_AXES: Array<{ value: GenerationSimilarity; label: string; description: string }> = [
  { value: 'faithful', label: 'Faithful', description: 'Same ad, product swapped — source image attached' },
  { value: 'copy_swap', label: 'Copy swap', description: 'Same look, new messaging angle — source image attached' },
  { value: 'composition_shift', label: 'Composition shift', description: 'Same copy and palette, new layout' },
  { value: 'mood_shift', label: 'Mood shift', description: 'Same layout and hook, new palette/lighting/lens' },
  { value: 'wild_card', label: 'Wild card', description: 'Keep only the hook — rebuild everything, may switch aspect' },
];

const AXIS_LABEL: Record<string, string> = Object.fromEntries(
  VARIATION_AXES.map(a => [a.value, a.label]),
);

interface GenerateDialogProps {
  open: boolean;
  /** One from the detail dialog, several from a library selection. */
  staticAdIds: string[];
  service: StaticAdsService;
  onClose: () => void;
  /** Called after the generations are queued. */
  onQueued: (count: number) => void;
}

export default function GenerateDialog({ open, staticAdIds, service, onClose, onQueued }: GenerateDialogProps) {
  const [products, setProducts] = useState<ProductSummary[] | null>(null);
  const [selected, setSelected] = useState<ProductSummary | null>(null);
  const [assets, setAssets] = useState<ProductAsset[] | null>(null);
  const [chosenAssetIds, setChosenAssetIds] = useState<Set<string>>(new Set());
  // A failed photo load is not "this product has no photos": with no selection
  // to send, the worker falls back to attaching EVERY photo the product has.
  const [assetsError, setAssetsError] = useState(false);
  // Selling points extracted from the product's brief. `chosen` maps the
  // ORIGINAL point text → the (possibly hand-tweaked) text this generation
  // will feature. Edits live here only — the stored brief and its extracted
  // points are never touched from this dialog.
  const [sellingPoints, setSellingPoints] = useState<string[] | null>(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  // Extraction failure is not "nothing extractable" — the empty-state copy
  // stays reserved for a genuinely point-free brief (the service's 404 path).
  const [pointsError, setPointsError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Map<string, string>>(new Map());
  const [editing, setEditing] = useState<string | null>(null);
  const [customPoint, setCustomPoint] = useState('');
  const [brief, setBrief] = useState('');
  // mood_shift is mechanically what the old single-select "fresh" default did.
  const [axes, setAxes] = useState<Set<GenerationSimilarity>>(new Set(['mood_shift']));
  // AI-baked text is the default. The prompt now hands the model a TEXT
  // ELEMENTS block — each string with its own role and type treatment — rather
  // than prose plus a loose list, which is what made baked text unreliable
  // enough to justify defaulting away from it.
  const [textMode, setTextMode] = useState<'ai' | 'overlay'>('ai');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After a partial submit, the exact (ad × axis) runs that never queued.
  // Retrying re-derives nothing: re-running the whole batch would queue — and
  // re-bill — the runs that already landed.
  const [pending, setPending] = useState<Array<{ staticAdId: string; axis: GenerationSimilarity }> | null>(null);
  const [queuedSoFar, setQueuedSoFar] = useState(0);
  // The group the already-queued runs joined; a retry must join the same one.
  const [groupId, setGroupId] = useState<string | undefined>(undefined);
  // Products failing to load must not present as an empty catalog — an empty
  // picker reads as "no products exist" and quietly forces clone mode.
  const [productsError, setProductsError] = useState(false);

  // The preview step: the exact prompt each run would send, editable. Nothing
  // renders without passing through here — a paid image built from a prompt
  // nobody read is how a bed ends up dressed in a comforter that isn't the
  // product being advertised.
  //
  // One entry per RUN, not one for the batch. A run's prompt is written from
  // its own ad (its own 1a/1b analyses) under its own variation axis, which
  // decides what the sheet redacts and whether the source ad is even attached
  // — so two runs genuinely cannot share a prompt.
  const [step, setStep] = useState<'configure' | 'preview'>('configure');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previews, setPreviews] = useState<PreviewEntry[]>([]);
  const [previewAt, setPreviewAt] = useState(0);

  const loadProducts = () => {
    setProducts(null);
    setProductsError(false);
    void service.listProducts().then((resp) => {
      if (resp) setProducts(resp.products || []);
      else setProductsError(true);
    });
  };

  useEffect(() => {
    if (!open) return;
    setStep('configure');
    setPreviews([]);
    setPreviewAt(0);
    setError(null);
    setAxes(new Set(['mood_shift']));
    setTextMode('ai');
    setPending(null);
    setQueuedSoFar(0);
    setGroupId(undefined);
    loadProducts();
  }, [open, service]); // eslint-disable-line react-hooks/exhaustive-deps

  // The product's photos, each individually attachable. All start attached —
  // deselecting is the exception, not the setup step.
  const loadAssets = () => {
    setAssets(null);
    setChosenAssetIds(new Set());
    setAssetsError(false);
    if (!open || !selected || !selected.assetCount) return;
    void service.listProductAssets(selected.id).then((resp) => {
      if (!resp) {
        setAssetsError(true);
        return;
      }
      const rows = resp.assets || [];
      setAssets(rows);
      setChosenAssetIds(new Set(rows.map(a => a.assetId)));
    });
  };
  useEffect(loadAssets, [open, selected?.id, service]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selling points, extracted server-side from the shared brief on first read
  // (a few seconds) and cached after. None start chosen: unchecked means the
  // build sheet keeps its own judgment about which claims fit the ad.
  const loadSellingPoints = () => {
    if (!selected || !selected.briefText) return;
    setPointsLoading(true);
    setPointsError(null);
    void service.getSellingPoints(selected.id)
      .then(resp => setSellingPoints(resp?.sellingPoints || []))
      .catch((err: any) => setPointsError(err?.response?.data?.error || err?.message || 'Extracting selling points failed'))
      .finally(() => setPointsLoading(false));
  };

  useEffect(() => {
    setSellingPoints(null);
    setChosen(new Map());
    setEditing(null);
    setCustomPoint('');
    setPointsError(null);
    if (!open || !selected || !selected.briefText) return;
    loadSellingPoints();
  }, [open, selected?.id, service]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePoint = (point: string) => {
    setChosen(prev => {
      const next = new Map(prev);
      if (next.has(point)) next.delete(point);
      else next.set(point, point);
      return next;
    });
    setEditing(null);
  };

  const toggleAxis = (axis: GenerationSimilarity) => {
    setAxes(prev => {
      const next = new Set(prev);
      if (next.has(axis)) next.delete(axis);
      else next.add(axis);
      return next;
    });
  };

  const toggleAsset = (assetId: string) => {
    setChosenAssetIds(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const addCustomPoint = () => {
    const p = customPoint.trim();
    if (!p) return;
    setChosen(prev => new Map(prev).set(p, p));
    setCustomPoint('');
  };

  const chosenPoints = () => Array.from(chosen.values()).map(sp => sp.trim()).filter(Boolean);
  const chosenAssets = () => (selected && assets
    ? assets.filter(a => chosenAssetIds.has(a.assetId)).map(a => a.assetId)
    : undefined);

  // Stable, declaration-ordered list of the checked axes.
  const selectedAxes = VARIATION_AXES.map(a => a.value).filter(v => axes.has(v));

  const count = staticAdIds.length;
  const totalRuns = count * selectedAxes.length;

  const targets = buildRuns(staticAdIds, selectedAxes, pending);

  // Approving a prompt approves it for THIS configuration; change any of it and
  // the approved text describes a setup that no longer exists.
  const configKey = previewConfigKey({
    staticAdIds, axes: selectedAxes, productId: selected?.id ?? null,
    assetIds: [...chosenAssetIds], sellingPoints: chosenPoints(), brief, textMode,
  });
  const approvedFor = useRef(configKey);
  useEffect(() => {
    if (approvedFor.current === configKey) return;
    approvedFor.current = configKey;
    setPreviews([]);
    setPreviewAt(0);
    setStep('configure');
  }, [configKey]);

  const loadPreview = async () => {
    setPreviewLoading(true);
    setError(null);
    setPreviews([]);
    setPreviewAt(0);
    setStep('preview');
    // Must match what the worker will actually attach: with no selection to
    // send (the photo list failed to load) it falls back to every photo the
    // product has, so the preview must not drop the fidelity block.
    const hasProductReferences = !!selected
      && (assetsError ? !!selected.assetCount : chosenAssetIds.size > 0);
    // Concurrent, because each run is an independent ~60s stage-2 call and
    // nothing about one informs another — run them one after another and five
    // runs is five minutes of waiting for what takes one. MAX_RUNS_PER_SUBMIT
    // caps the fan-out at 5, which is well inside what the API will take.
    const one = async (target: Run): Promise<PreviewEntry> => {
      try {
        const resp = await service.generationPreview(target.staticAdId, {
          productId: selected ? String(selected.id) : undefined,
          sellingPoints: chosen.size ? chosenPoints() : undefined,
          remakeBrief: brief.trim() || undefined,
          similarity: target.axis,
          textMode,
          hasProductReferences,
        });
        return {
          ...target,
          prompt: resp.prompt,
          aspect: resp.aspectRatio,
          sheet: resp.buildSheet || null,
          unfilled: resp.unfilledSlots || [],
          needsPhoto: !!resp.needsProductPhoto,
          textElements: resp.textElements || [],
        };
      } catch (err: any) {
        // One run's stage 2 failing must not cost the others their preview.
        return {
          ...target,
          prompt: '',
          aspect: null,
          sheet: null,
          unfilled: [],
          needsPhoto: false,
          textElements: [],
          error: err?.response?.data?.error || err?.message || 'Preview failed',
        };
      }
    };
    try {
      // Seeded in target order and filled in place, so a run that finishes
      // second does not take the first one's tab. Each lands as it arrives —
      // the tabs are readable while the rest are still being written.
      const started = targets.map(one);
      await Promise.all(started.map(async (promise, i) => {
        const entry = await promise;
        setPreviews((prev) => {
          const next = prev.length ? [...prev] : targets.map(() => null as unknown as PreviewEntry);
          next[i] = entry;
          return next;
        });
      }));
    } finally {
      setPreviewLoading(false);
    }
  };

  const editPrompt = (index: number, prompt: string) =>
    setPreviews(prev => prev.map((entry, i) => (i === index ? { ...entry, prompt } : entry)));

  // Slots fill as their run lands, so the array is sparse while previews are in
  // flight — every consumer has to tolerate a hole rather than assume a run.
  const landed = previews.filter(Boolean);
  const readyPreviews = landed.filter(entry => !entry.error && entry.prompt.trim());
  const current = previews[previewAt];

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // A multi-run submit is one creative exploration, so its outputs start
      // life as a group — shared launch copy, one batch publish. Grouping is
      // best-effort: if the group can't be created the runs still queue,
      // just ungrouped. A retry joins the group the first attempt made.
      let group = groupId;
      if (!group && queuedSoFar + targets.length > 1) {
        try {
          const created = await service.createGenerationGroup();
          group = created.groupId;
          setGroupId(group);
        } catch (err) {
          console.error('Creating the generation group failed — queueing ungrouped', err);
        }
      }
      // One generation per (ad × axis); failures reported per run rather than
      // aborting the batch — the queued ones are already queued.
      const failures: string[] = [];
      const stillPending: Array<{ staticAdId: string; axis: GenerationSimilarity }> = [];
      let queued = queuedSoFar;
      for (const target of targets) {
        const approved = approvedPrompt(landed, target);
        // Nothing renders unreviewed: a run whose stage 2 failed has no
        // approved prompt, and queueing it would silently re-run stage 2 and
        // render from a prompt no one saw.
        if (!approved) {
          failures.push(`${target.axis}: no approved prompt — preview it again`);
          stillPending.push(target);
          continue;
        }
        try {
          await service.createGeneration({
            staticAdId: target.staticAdId,
            productId: selected ? String(selected.id) : undefined,
            assetIds: chosenAssets(),
            sellingPoints: chosen.size ? chosenPoints() : undefined,
            remakeBrief: brief.trim() || undefined,
            similarity: target.axis,
            textMode,
            groupId: group,
            // THIS run's approved prompt, looked up by run — never a shared
            // one. The old code spread a single `previewPrompt` into every
            // iteration of this loop; that was only ever correct because the
            // preview button refused to appear unless there was exactly one
            // run, and sending one ad's prompt for another ad's render is the
            // kind of wrong that still produces a plausible-looking image.
            ...(approved ? {
              promptOverride: approved.prompt.trim(),
              aspectRatio: approved.aspect,
              buildSheet: approved.sheet || undefined,
            } : {}),
          });
          queued += 1;
        } catch (err: any) {
          failures.push(err?.response?.data?.error || err?.message || target.staticAdId);
          stillPending.push(target);
        }
      }
      setQueuedSoFar(queued);
      setPending(stillPending.length ? stillPending : null);
      // If queue failures left fewer than 2 members, drop the group: a
      // 1-member group is just a slow card (a folded stack of one) with
      // nothing to share copy across. Only once nothing is left to retry —
      // a retry would refill it. Best-effort: a leftover group is cosmetic.
      if (group && queued < 2 && !stillPending.length) {
        try {
          await service.deleteGenerationGroup(group);
        } catch (err) {
          console.error('Cleaning up the underfilled generation group failed', err);
        }
      }
      if (failures.length) {
        // Whatever queued is real and already spending, so the activity strip
        // has to start moving even on a partial submit.
        if (queued) bumpStaticActivity();
        setError(`${failures.length} of ${targets.length} failed to queue: ${failures[0]}`
          + (queued ? ` ${queued} ${queued === 1 ? 'is' : 'are'} already queued and running — Retry sends only the ${stillPending.length} that failed.` : '')
          + ' To change the setup instead, cancel and start over.');
      } else {
        bumpStaticActivity();
        onQueued(queued);
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy || previewLoading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {step === 'preview'
          ? `Review ${totalRuns === 1 ? 'the prompt' : `${totalRuns} prompts`}`
          : `Generate creative from ${count === 1 ? 'this ad' : `${count} ads`}`}
        {step === 'preview' && current?.aspect && (
          <Chip size="small" label={`${current.aspect} aspect`} sx={{ ml: 1 }} />
        )}
      </DialogTitle>

      {step === 'preview' ? (
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This exact text goes to the image model. Edit freely — your edits apply to this
            generation only.
          </Typography>

          {totalRuns > 1 && (
            // Each run has its own prompt, written from its own ad under its
            // own variation — so each is reviewed on its own tab rather than
            // one standing in for the rest.
            <Tabs
              value={Math.min(previewAt, Math.max(previews.length - 1, 0))}
              onChange={(_e: React.SyntheticEvent, v: number) => setPreviewAt(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
            >
              {targets.map((target, i) => {
                const entry = previews[i];
                return (
                  <Tab
                    key={runKey(target.staticAdId, target.axis)}
                    disabled={!entry}
                    label={(
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{`${i + 1}. ${AXIS_LABEL[target.axis] || target.axis}`}</span>
                        {!entry && <CircularProgress size={11} />}
                        {entry?.error && <ErrorOutlineIcon fontSize="small" color="error" />}
                      </Stack>
                    )}
                  />
                );
              })}
            </Tabs>
          )}

          {previewLoading && (
            <Alert severity="info" sx={{ mb: 1 }} icon={<CircularProgress size={16} />}>
              Writing {targets.length} prompt{targets.length === 1 ? '' : 's'} at once
              (~60s) — {landed.length} of {targets.length} done. Each appears as it lands;
              you can start reading the ones already here.
            </Alert>
          )}

          {current?.error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              This run&apos;s prompt could not be written: {current.error}. Go back and preview
              again — nothing will be generated for it.
            </Alert>
          )}
          {current && !current.error && current.unfilled.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              The analysis left {current.unfilled.length} part{current.unfilled.length === 1 ? '' : 's'} of
              this ad blank ({current.unfilled.join(', ')}). The render will still cost money, but it
              will not reproduce this ad. Fill the gap in the prompt below, or re-analyze the ad first.
            </Alert>
          )}
          {current?.needsPhoto && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              This build sheet expects a product photo. Without one attached the model will invent a
              product — pick a product and tick at least one photo before generating.
            </Alert>
          )}
          {/* The image bakes its own text in this mode, so the prompt should end
              with a TEXT ELEMENTS block naming every string and how it is set.
              None means stage 2 ignored the format and the copy is loose in the
              prose again — which is what put a headline in the wrong corner. */}
          {current && !current.error && textMode === 'ai' && !current.textElements.length && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              This prompt has no TEXT ELEMENTS block, so the wording and its type treatment are
              scattered through the scene description — the model has to guess which string is the
              headline. It will still render; expect the copy to land loosely.
            </Alert>
          )}

          {current && !current.error && (
            <TextField
              fullWidth multiline minRows={12} maxRows={24} value={current.prompt}
              onChange={e => editPrompt(previewAt, e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.5 } }}
            />
          )}
          {!current && !previewLoading && (
            <Alert severity="warning">No prompt was written. Go back and try again.</Alert>
          )}
        </DialogContent>
      ) : (
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Rebuilds {count === 1 ? "the ad's" : "each ad's"} structure around the chosen product.
              The product&apos;s reference photos are attached to the generation and treated as ground
              truth — the model is told not to deviate from them.
            </Typography>

            {productsError && (
              <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={loadProducts}>Retry</Button>}
              >
                Couldn&apos;t load the product catalog, so no product can be picked. Cloning
                {count === 1 ? ' this ad' : ' these ads'} as-is still works — only a remake built
                around one of our products is blocked.
              </Alert>
            )}

            <Autocomplete
              size="small"
              options={products || []}
              value={selected}
              onChange={(_e, next) => setSelected(next)}
              disabled={productsError}
              loading={!products && !productsError}
              getOptionLabel={p => p.title}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(liProps, p) => (
                <li {...liProps} key={p.id}>
                  {p.title}{p.assetCount ? '' : ' — no reference photos'}
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Product"
                  placeholder="Search products — leave empty to clone the ad as-is"
                  helperText="The product the remake advertises (the shared Shopify catalog)"
                />
              )}
            />

            {selected && assets && assets.length > 0 && (
              <ProductAssetPicker assets={assets} chosen={chosenAssetIds} onToggle={toggleAsset} />
            )}

            {selected && assetsError && (
              <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={loadAssets}>Retry</Button>}
              >
                Couldn&apos;t load {selected.title}&apos;s reference photos, so none can be picked.
                Generating now attaches <strong>all</strong> of this product&apos;s photos — Retry
                first if you meant to choose which ones.
              </Alert>
            )}

            {selected && selected.briefText && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Selling points to feature {chosen.size ? `(${chosen.size} chosen)` : '(optional)'}
                </Typography>
                {pointsLoading && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="body2" color="text.secondary">Extracting from the product brief…</Typography>
                  </Stack>
                )}
                {pointsError && !pointsLoading && (
                  <Alert
                    severity="error" sx={{ mt: 0.5 }}
                    action={<Button color="inherit" size="small" onClick={loadSellingPoints}>Retry</Button>}
                  >
                    {pointsError}
                  </Alert>
                )}
                {sellingPoints && !sellingPoints.length && !pointsLoading && !pointsError && (
                  <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                    Nothing extractable from this brief.
                  </Typography>
                )}
                {/* Half the height it was: the list scrolls, and every row above
                    it (product, photos, variations, text handling) was being
                    pushed off the dialog by a brief with a dozen points. */}
                <Box sx={{ mt: 0.25, maxHeight: 120, overflowY: 'auto', pr: 0.5 }}>
                  {[...(sellingPoints || []),
                    ...Array.from(chosen.keys()).filter(k => !(sellingPoints || []).includes(k))].map(point => {
                    const isChosen = chosen.has(point);
                    const value = chosen.get(point) ?? point;
                    const edited = isChosen && value !== point;
                    return (
                      <Box key={point} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                        <Checkbox
                          size="small" checked={isChosen} onChange={() => togglePoint(point)}
                          sx={{ p: 0.2 }}
                        />
                        {editing === point ? (
                          <TextField
                            size="small" fullWidth autoFocus value={value}
                            onChange={e => setChosen(prev => new Map(prev).set(point, e.target.value))}
                            onBlur={() => setEditing(null)}
                            onKeyDown={e => { if (e.key === 'Enter') setEditing(null); }}
                            helperText="Applies to this generation only — the stored selling point is unchanged"
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            onClick={() => togglePoint(point)}
                            sx={{
                              flex: 1, minWidth: 0, fontSize: '0.78rem', lineHeight: 1.3, py: 0,
                              cursor: 'pointer', fontStyle: edited ? 'italic' : 'normal',
                            }}
                          >
                            {value}{edited ? ' (edited)' : ''}
                          </Typography>
                        )}
                        {isChosen && editing !== point && (
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setEditing(point)}>
                            <EditOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                  <TextField
                    size="small" fullWidth placeholder="Add your own — e.g. 20% off this week"
                    value={customPoint}
                    onChange={e => setCustomPoint(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addCustomPoint(); }}
                  />
                  <Button size="small" disabled={!customPoint.trim()} onClick={addCustomPoint}>
                    Add
                  </Button>
                </Stack>
              </Box>
            )}

            {selected && assets && assets.length > 0 && chosenAssetIds.size === 0 && (
              <Alert severity="warning">
                No photos selected — no product reference photo will be attached, so the product in
                the generated ad will be invented from the text description.
              </Alert>
            )}

            {selected && !selected.assetCount && (
              // Proceeding is allowed — sometimes a lookalike is fine — but never
              // silently: without reference photos the product in the output is
              // INVENTED from text, which is the exact failure the catalog exists
              // to prevent when it matters.
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit" size="small"
                    component={RouterLink} to="/static-ads/products"
                    onClick={onClose}
                  >
                    Add photos
                  </Button>
                }
              >
                <strong>{selected.title} has no reference photos.</strong> No product reference photo
                will be used — the product in the generated ad will be invented from the text description and
                may not match the real product. If that&apos;s OK, generate anyway; if not, add photos
                on the Products tab first.
              </Alert>
            )}

            {selected && chosen.size === 0 && !brief.trim() && (
              <Alert severity="info">
                No selling points chosen and no direction typed — the remake will reuse the source
                ad&apos;s own copy angles, just rebuilt around {selected.title}. Tick a point and it
                replaces the original wording wherever it makes the same kind of claim.
              </Alert>
            )}

            <TextField
              select size="small" label="Text handling" value={textMode}
              onChange={e => setTextMode(e.target.value as 'ai' | 'overlay')}
              helperText={textMode === 'overlay'
                ? 'The image is generated text-free; AI places the copy as an overlay you can edit afterwards (Output → Edit text). Text is always spelled correctly.'
                : 'The prompt names every string, its role and its type treatment, and you can edit that block before generating. Spelling is still the image model\'s, and the result is not editable after.'}
            >
              <MenuItem value="ai">AI renders text in the image (recommended)</MenuItem>
              <MenuItem value="overlay">Editable overlay — real text on a clean image</MenuItem>
            </TextField>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Variations to run {selectedAxes.length ? `(${selectedAxes.length} chosen)` : ''}
              </Typography>
              {VARIATION_AXES.map(axis => (
                <Box key={axis.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Checkbox
                    size="small" checked={axes.has(axis.value)} onChange={() => toggleAxis(axis.value)}
                    sx={{ p: 0.4 }}
                  />
                  <Typography
                    variant="body2"
                    onClick={() => toggleAxis(axis.value)}
                    sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem', py: 0.25, cursor: 'pointer' }}
                  >
                    <strong>{axis.label}</strong> — {axis.description}
                  </Typography>
                </Box>
              ))}
              {!selectedAxes.length && (
                <Typography variant="caption" color="error">
                  Pick at least one variation — each checked one queues its own generation per ad.
                </Typography>
              )}
            </Box>

            {totalRuns > MAX_RUNS_PER_SUBMIT && (
              <Alert severity="warning">
                Up to {MAX_RUNS_PER_SUBMIT} generations at a time — this selection is {totalRuns} ({count} ad{count === 1 ? '' : 's'} × {selectedAxes.length} variation{selectedAxes.length === 1 ? '' : 's'}). Deselect ads or variations.
              </Alert>
            )}

            <TextField
              size="small" label="Extra direction (optional)" value={brief} multiline minRows={2} maxRows={6}
              onChange={e => setBrief(e.target.value)}
              helperText="Anything else the creative should say or do. Only this text and the chosen selling points are sent."
            />
          </Stack>
        </DialogContent>
      )}

      <DialogActions>
        {step === 'preview' ? (
          <>
            <Button onClick={() => setStep('configure')} disabled={busy || previewLoading}>Back</Button>
            <Box sx={{ flex: 1 }} />
            {readyPreviews.length > 0 && readyPreviews.length < targets.length && !previewLoading && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                {targets.length - readyPreviews.length} of {targets.length} have no prompt and will be skipped
              </Typography>
            )}
            <Button
              variant="contained" onClick={submit}
              disabled={busy || previewLoading || !readyPreviews.length}
            >
              {busy ? 'Queueing…'
                : `Generate ${readyPreviews.length} creative${readyPreviews.length === 1 ? '' : 's'}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy || previewLoading}>Cancel</Button>
            <Box sx={{ flex: 1 }} />
            {/* The only way forward. Nothing renders from a prompt nobody read
                — and this costs nothing extra: the worker skips stage 2 when
                an approved prompt arrives, so the bill just moves earlier. */}
            <Button
              variant="contained" onClick={loadPreview}
              disabled={busy || previewLoading || (!products && !productsError)
                || !selectedAxes.length || totalRuns > MAX_RUNS_PER_SUBMIT}
              startIcon={previewLoading ? <CircularProgress size={14} /> : undefined}
            >
              {previewLoading ? 'Writing prompts (~30–60s each)…'
                : pending ? `Review ${pending.length} that failed`
                  : `Review ${totalRuns} prompt${totalRuns === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
      </DialogActions>
      {error && <Alert severity="error" sx={{ m: 2, mt: 0 }}>{error}</Alert>}
    </Dialog>
  );
}
